import React, {useCallback, useState} from 'react';

import {Button} from '../../../../Button';
import {TextField} from '../../../../Form/TextField';
import {WaveLoader} from '../../../../Loaders/WaveLoader';
import {Tailwind} from '../../../../Tailwind';
import {Messages} from '../pages/ChatView/types';
import {ResizableDrawer} from '../pages/common/TailwindResizableDrawer';
import {
  CREATE_PROMPT_ACTIONS,
  CreatePromptProvider,
  useCreatePromptContext,
} from './CreatePromptDrawerContext';
import {MessageEditor} from './MessageEditor';
import {MessageVariables} from './MessageVariables';
import {validatePromptName} from './promptNameValidation';

interface CreatePromptDrawerProps {
  open: boolean;
  onClose: () => void;
  onSavePrompt: (name: string, rows: any[]) => void;
  isCreating?: boolean;
  initialMessages?: Messages;
}

export const CreatePromptDrawer: React.FC<CreatePromptDrawerProps> = ({
  open,
  onClose,
  onSavePrompt,
  isCreating = false,
  initialMessages,
}) => {
  return (
    <CreatePromptProvider
      onPublishPrompt={onSavePrompt}
      initialMessages={initialMessages}>
      <CreatePromptDrawerContent
        open={open}
        onClose={onClose}
        isCreating={isCreating}
      />
    </CreatePromptProvider>
  );
};

const CreatePromptDrawerContent: React.FC<{
  open: boolean;
  onClose: () => void;
  isCreating?: boolean;
}> = ({open, onClose, isCreating = false}) => {
  const {state, dispatch, handleCloseDrawer, handlePublishPrompt} =
    useCreatePromptContext();

  const {promptName, messages, isLoading, error, drawerWidth, isFullscreen} =
    state;

  const setMessages = useCallback(
    (messages: Messages) => {
      dispatch({
        type: CREATE_PROMPT_ACTIONS.SET_MESSAGES,
        payload: messages,
      });
    },
    [dispatch]
  );

  // Note: Initialization is handled in the context provider based on initialMessages prop

  const [nameError, setNameError] = useState<string | null>(null);

  const handleNameChange = useCallback(
    (value: string) => {
      dispatch({
        type: CREATE_PROMPT_ACTIONS.SET_PROMPT_NAME,
        payload: value,
      });

      const validationResult = validatePromptName(value);
      setNameError(validationResult.error);
    },
    [dispatch]
  );

  const wrappedOnClose = useCallback(() => {
    handleCloseDrawer();
    onClose();
  }, [handleCloseDrawer, onClose]);

  const handleToggleFullscreen = useCallback(() => {
    dispatch({
      type: CREATE_PROMPT_ACTIONS.SET_IS_FULLSCREEN,
      payload: !isFullscreen,
    });
  }, [dispatch, isFullscreen]);

  return (
    <Tailwind>
      <ResizableDrawer
        data-testid="create-prompt-drawer"
        open={open}
        onClose={wrappedOnClose}
        usePortal={true}
        defaultWidth={isFullscreen ? window.innerWidth - 73 : drawerWidth}
        setWidth={width =>
          !isFullscreen &&
          dispatch({
            type: CREATE_PROMPT_ACTIONS.SET_DRAWER_WIDTH,
            payload: width,
          })
        }
        headerContent={
          <div className="tw-style flex h-[44px] min-h-[44px] flex-shrink-0 items-center justify-between border-b border-moon-250 pl-16 pr-8 dark:border-moon-250">
            <h2 className="dark:text-black font-['Source_Sans_Pro'] text-[20px] font-semibold text-moon-950">
              Create new prompt
            </h2>
            <div className="flex gap-8">
              <Button
                onClick={handleToggleFullscreen}
                variant="ghost"
                icon={
                  isFullscreen ? 'minimize-mode' : 'full-screen-mode-expand'
                }
                tooltip={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}
              />
              <Button
                onClick={wrappedOnClose}
                variant="ghost"
                icon="close"
                tooltip="Close"
              />
            </div>
          </div>
        }>
        <div className="tw-style flex flex-1 flex-col overflow-hidden">
          {isCreating || isLoading ? (
            <div className="flex flex-1 items-center justify-center">
              <WaveLoader size="huge" />
            </div>
          ) : (
            <>
              <div className="flex min-h-0 flex-col flex-1 overflow-auto p-16">
                {error && (
                  <div className="mb-16 rounded bg-red-300 p-16 text-red-700 dark:bg-red-700 dark:text-red-300">
                    <p className="font-['Source_Sans_Pro']">{error}</p>
                  </div>
                )}

                <div className="mb-16">
                  <label className="mb-8 block text-left font-['Source_Sans_Pro'] font-semibold text-moon-950 dark:text-moon-950">
                    Prompt name
                  </label>
                  <TextField
                    value={promptName}
                    onChange={handleNameChange}
                    placeholder="Enter a name for your prompt"
                    errorState={nameError !== null}
                  />
                  {nameError && (
                    <p className="text-14 mt-8 font-['Source_Sans_Pro'] text-red-500 dark:text-red-400">
                      {nameError}
                    </p>
                  )}
                  <p className="mt-8 text-left font-['Source_Sans_Pro'] text-[14px] font-normal text-moon-500 dark:text-moon-400">
                    Valid prompt names must start with a letter or number and
                    can only contain letters, numbers, hyphens, and underscores.
                  </p>
                </div>

                <div className="mb-24">
                  <label className="mb-8 block text-left font-['Source_Sans_Pro'] font-semibold text-moon-950 dark:text-moon-950">
                    Messages
                  </label>

                  <MessageEditor
                    messages={messages}
                    setMessages={setMessages}
                  />

                  {/* Variables Section */}
                  <MessageVariables messages={messages} />
                </div>

                {/* Spacer */}
                <div className="min-h-[24px]"></div>
              </div>

              <div className="block flex w-full flex-shrink-0 border-t border-moon-250 bg-white px-16 py-16 dark:border-moon-250 dark:bg-moon-100">
                <Button
                  data-testid="create-prompt-submit-button"
                  onClick={handlePublishPrompt}
                  variant="primary"
                  disabled={
                    !promptName || nameError !== null || messages.length === 0
                  }
                  tooltip="Save and publish the prompt"
                  style={{
                    width: '100%',
                  }}
                  twWrapperStyles={{
                    width: '100%',
                    display: 'block',
                  }}>
                  Publish Prompt
                </Button>
              </div>
            </>
          )}
        </div>
      </ResizableDrawer>
    </Tailwind>
  );
};
